import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Check, X, Crown, Zap, Shield, Star, Award } from 'lucide-react';
import NearULogo from '../components/NearULogo';

const MembershipPage = () => {
  const navigate = useNavigate();
  const [currentTier, setCurrentTier] = useState('elite'); // Simulated current membership

  const tiers = [
    {
      id: 'free',
      name: 'Free Membership',
      price: 'Free',
      icon: Shield,
      color: 'rgba(244, 231, 195, 0.3)',
      features: [
        { text: 'Basic profile', included: true },
        { text: 'Can see hotspots', included: true },
        { text: 'Can see public events but cannot sign up (Host must invite)', included: true },
        { text: 'Limited messaging', included: true },
        { text: 'Advanced filters', included: false },
        { text: 'Private settings', included: false },
        { text: 'See who viewed your profile', included: false }
      ]
    },
    {
      id: 'basic',
      name: 'Basic Membership',
      price: '$29.99',
      priceLabel: 'per month',
      icon: Zap,
      color: 'rgba(212, 175, 55, 0.4)',
      popular: false,
      features: [
        { text: 'Basic profile', included: true },
        { text: 'Can see hotspots', included: true },
        { text: 'Can see public events and register', included: true },
        { text: 'Unlimited messaging', included: true },
        { text: 'Advanced filters', included: true },
        { text: 'Private settings', included: true },
        { text: 'See who viewed your profile', included: true },
        { text: 'Add hotspots', included: false },
        { text: 'Create events', included: false },
        { text: 'VIP badge', included: false }
      ]
    },
    {
      id: 'elite',
      name: 'Elite Membership',
      price: '$49.99',
      priceLabel: 'per month',
      icon: Crown,
      color: 'rgba(212, 175, 55, 0.6)',
      popular: true,
      features: [
        { text: 'Basic profile', included: true },
        { text: 'Can see hotspots and add hotspots', included: true },
        { text: 'Can see public events and register', included: true },
        { text: 'Can create events', included: true },
        { text: 'Unlimited messaging', included: true },
        { text: 'Advanced filters', included: true },
        { text: 'Private settings', included: true },
        { text: 'See who viewed your profile', included: true },
        { text: 'VIP badge on profile', included: true },
        { text: 'Exclusive hotspot access', included: true },
        { text: 'Featured in search results', included: true },
        { text: 'Advanced privacy controls', included: true },
        { text: 'Priority customer support', included: true },
        { text: 'Event host tools', included: true },
        { text: 'Income verification access', included: true }
      ]
    }
  ];

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #0D0D0D 0%, #2A1B0E 100%)', paddingBottom: '80px' }}>
      <div className="page-header">
        <button
          onClick={() => navigate('/profile')}
          style={{
            background: 'none',
            border: 'none',
            color: '#D4AF37',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '12px',
            fontSize: '14px',
            padding: '8px 0'
          }}
        >
          <ChevronLeft size={20} />
          Back to Profile
        </button>
        <div style={{ marginBottom: '12px' }}>
          <NearULogo size="small" showText={true} />
        </div>
        <h1 className="page-title" style={{ fontFamily: 'Cinzel, Playfair Display, serif' }}>
          MEMBERSHIP
        </h1>
        <p className="page-subtitle" style={{ letterSpacing: '1px' }}>Choose your perfect plan</p>
      </div>

      <div style={{ padding: '24px' }}>
        <div style={{ 
          display: 'grid', 
          gap: '24px',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          {tiers.map((tier) => {
            const Icon = tier.icon;
            const isCurrent = currentTier === tier.id;
            
            return (
              <div
                key={tier.id}
                style={{
                  background: 'linear-gradient(145deg, rgba(28, 28, 28, 0.95), rgba(13, 13, 13, 0.98))',
                  backdropFilter: 'blur(30px)',
                  borderRadius: '24px',
                  padding: '32px',
                  border: `2px solid ${isCurrent ? '#D4AF37' : 'rgba(212, 175, 55, 0.3)'}`,
                  boxShadow: isCurrent 
                    ? '0 12px 48px rgba(212, 175, 55, 0.3), inset 0 1px 0 rgba(212, 175, 55, 0.2)'
                    : '0 8px 32px rgba(0, 0, 0, 0.6)',
                  position: 'relative',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                {tier.popular && (
                  <div style={{
                    position: 'absolute',
                    top: '-12px',
                    right: '24px',
                    background: 'linear-gradient(135deg, #D4AF37, #B8941F)',
                    padding: '6px 16px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    color: '#0D0D0D',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    boxShadow: '0 4px 12px rgba(212, 175, 55, 0.4)'
                  }}>
                    Most Popular
                  </div>
                )}

                {isCurrent && (
                  <div style={{
                    position: 'absolute',
                    top: '24px',
                    right: '24px',
                    background: 'rgba(80, 200, 120, 0.2)',
                    padding: '6px 12px',
                    borderRadius: '16px',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    color: '#50C878',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    border: '1px solid rgba(80, 200, 120, 0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <Check size={14} />
                    Current Plan
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
                  <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${tier.color}, transparent)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid rgba(212, 175, 55, 0.4)',
                    boxShadow: '0 4px 16px rgba(212, 175, 55, 0.2)'
                  }}>
                    <Icon size={32} style={{ color: '#D4AF37' }} />
                  </div>
                  <div>
                    <h2 style={{
                      fontSize: '28px',
                      fontFamily: 'Cinzel, serif',
                      color: '#F4E7C3',
                      marginBottom: '8px'
                    }}>
                      {tier.name}
                    </h2>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                      <span style={{
                        fontSize: '36px',
                        fontWeight: 'bold',
                        color: '#D4AF37'
                      }}>
                        {tier.price}
                      </span>
                      {tier.priceLabel && (
                        <span style={{
                          fontSize: '14px',
                          color: 'rgba(244, 231, 195, 0.7)'
                        }}>
                          {tier.priceLabel}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: '32px' }}>
                  <div style={{
                    height: '1px',
                    background: 'linear-gradient(90deg, transparent, rgba(212, 175, 55, 0.3), transparent)',
                    marginBottom: '24px'
                  }} />
                  
                  <div style={{ display: 'grid', gap: '12px' }}>
                    {tier.features.map((feature, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '12px',
                          opacity: feature.included ? 1 : 0.4
                        }}
                      >
                        {feature.included ? (
                          <Check size={20} style={{ 
                            color: '#50C878', 
                            flexShrink: 0,
                            marginTop: '2px',
                            filter: 'drop-shadow(0 0 4px rgba(80, 200, 120, 0.6))'
                          }} />
                        ) : (
                          <X size={20} style={{ 
                            color: 'rgba(244, 231, 195, 0.3)', 
                            flexShrink: 0,
                            marginTop: '2px'
                          }} />
                        )}
                        <span style={{
                          color: feature.included ? '#F4E7C3' : 'rgba(244, 231, 195, 0.5)',
                          fontSize: '15px',
                          lineHeight: '1.5'
                        }}>
                          {feature.text}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {!isCurrent && (
                  <button
                    className="btn-primary"
                    style={{ width: '100%' }}
                    onClick={() => alert(`Upgrading to ${tier.name}. Payment integration will be implemented.`)}
                  >
                    {tier.id === 'free' ? 'Downgrade to Free' : `Upgrade to ${tier.name}`}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <div style={{
          marginTop: '48px',
          padding: '24px',
          background: 'rgba(212, 175, 55, 0.08)',
          borderRadius: '20px',
          border: '1px solid rgba(212, 175, 55, 0.2)',
          maxWidth: '800px',
          margin: '48px auto 0'
        }}>
          <h3 style={{
            fontSize: '20px',
            color: '#D4AF37',
            fontFamily: 'Cinzel, serif',
            marginBottom: '16px',
            textAlign: 'center'
          }}>
            Frequently Asked Questions
          </h3>
          <div style={{ color: '#F4E7C3', fontSize: '14px', lineHeight: '1.8' }}>
            <p style={{ marginBottom: '12px' }}>
              <strong>Can I cancel anytime?</strong><br />
              Yes, you can cancel your subscription at any time. Your access will continue until the end of your billing period.
            </p>
            <p style={{ marginBottom: '12px' }}>
              <strong>What payment methods do you accept?</strong><br />
              We accept all major credit cards, debit cards, and digital payment methods.
            </p>
            <p>
              <strong>Is my payment information secure?</strong><br />
              Absolutely. We use industry-standard encryption and never store your full payment details.
            </p>
          </div>
        </div>

        {/* Income Verification - Coming Soon */}
        <div 
          data-testid="income-verification-coming-soon"
          style={{
            marginTop: '32px',
            padding: '24px',
            background: 'rgba(107, 70, 193, 0.08)',
            borderRadius: '20px',
            border: '1px solid rgba(107, 70, 193, 0.25)',
            maxWidth: '800px',
            margin: '32px auto 0'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <h3 style={{
              fontSize: '20px',
              color: '#F4E7C3',
              fontFamily: 'Cinzel, serif',
              margin: 0
            }}>
              Income Verification
            </h3>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              background: 'linear-gradient(135deg, #6B46C1 0%, #553C9A 100%)',
              borderRadius: '12px'
            }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#E9D8FD" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12,6 12,12 16,14" />
              </svg>
              <span style={{
                fontSize: '11px',
                fontWeight: '600',
                color: '#E9D8FD',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Coming Soon
              </span>
            </div>
          </div>
          
          <p style={{ color: 'rgba(244, 231, 195, 0.8)', fontSize: '14px', lineHeight: '1.7', marginBottom: '20px' }}>
            Diamond tier members will soon be able to verify their income to unlock exclusive high-roller features 
            and connect with other verified high-net-worth individuals.
          </p>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px'
          }}>
            <div style={{
              padding: '16px',
              background: 'rgba(107, 70, 193, 0.1)',
              borderRadius: '12px',
              border: '1px solid rgba(107, 70, 193, 0.2)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <Shield size={16} color="#9F7AEA" />
                <span style={{ color: '#9F7AEA', fontSize: '13px', fontWeight: '600' }}>Verified Badge</span>
              </div>
              <p style={{ color: 'rgba(244, 231, 195, 0.7)', fontSize: '13px', margin: 0 }}>
                Display income tier badge on your profile
              </p>
            </div>
            <div style={{
              padding: '16px',
              background: 'rgba(107, 70, 193, 0.1)',
              borderRadius: '12px',
              border: '1px solid rgba(107, 70, 193, 0.2)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <Star size={16} color="#9F7AEA" />
                <span style={{ color: '#9F7AEA', fontSize: '13px', fontWeight: '600' }}>Elite Access</span>
              </div>
              <p style={{ color: 'rgba(244, 231, 195, 0.7)', fontSize: '13px', margin: 0 }}>
                Exclusive events and VIP experiences
              </p>
            </div>
            <div style={{
              padding: '16px',
              background: 'rgba(107, 70, 193, 0.1)',
              borderRadius: '12px',
              border: '1px solid rgba(107, 70, 193, 0.2)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <Award size={16} color="#9F7AEA" />
                <span style={{ color: '#9F7AEA', fontSize: '13px', fontWeight: '600' }}>Premium Matching</span>
              </div>
              <p style={{ color: 'rgba(244, 231, 195, 0.7)', fontSize: '13px', margin: 0 }}>
                Connect with other verified members
              </p>
            </div>
          </div>
          
          <button
            disabled
            style={{
              width: '100%',
              marginTop: '20px',
              padding: '14px',
              background: 'rgba(107, 70, 193, 0.2)',
              color: 'rgba(159, 122, 234, 0.6)',
              border: '1px solid rgba(107, 70, 193, 0.3)',
              borderRadius: '12px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12,6 12,12 16,14" />
            </svg>
            Notify Me When Available
          </button>
        </div>
      </div>
    </div>
  );
};

export default MembershipPage;
